import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [statsData, providersData] = await Promise.all([
        api.getStats(),
        api.getProviderStatus(),
      ]);
      setStats(statsData.overview);
      setProviders(providersData.providers);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <Link to="/providers" className="btn btn-primary">+ Add Provider</Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalRequests.toLocaleString()}</div>
            <div className="stat-label">Total Requests</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.successRate}%</div>
            <div className="stat-label">Success Rate</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalTokens.toLocaleString()}</div>
            <div className="stat-label">Tokens Used</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.avgResponseTimeMs}ms</div>
            <div className="stat-label">Avg Response</div>
          </div>
        </div>
      )}

      {/* Providers Overview */}
      <div className="card">
        <h2>Provider Status</h2>
        {providers.length === 0 ? (
          <div className="empty-state">
            <p>No providers configured yet.</p>
            <Link to="/providers" className="btn btn-primary">Add Your First Provider</Link>
          </div>
        ) : (
          <div className="provider-list">
            {providers.map(provider => (
              <div key={provider.id} className={`provider-item ${provider.is_active ? '' : 'provider-inactive'}`}>
                <div className="provider-info">
                  <span className="provider-name">{provider.name}</span>
                  <span className="provider-model">{provider.model_name}</span>
                  {provider.alias && <span className="provider-alias">→ {provider.alias}</span>}
                </div>
                <div className="provider-usage">
                  <div className="usage-bar">
                    <div
                      className="usage-fill"
                      style={{ width: `${Math.min(provider.usage_percent, 100)}%` }}
                    />
                  </div>
                  <span className="usage-text">
                    {provider.requests_used}/{provider.request_limit || '∞'} req
                  </span>
                </div>
                <div className={`provider-status ${provider.is_active ? 'status-active' : 'status-inactive'}`}>
                  {provider.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Start */}
      <div className="card">
        <h2>Quick Start</h2>
        <div className="quick-start">
          <div className="quick-start-step">
            <span className="step-number">1</span>
            <p>Add your free API provider endpoints in the <Link to="/providers">Providers</Link> page</p>
          </div>
          <div className="quick-start-step">
            <span className="step-number">2</span>
            <p>Point your OpenAI-compatible client to:</p>
            <code className="code-block">POST http://localhost:3001/api/proxy/v1/chat/completions</code>
          </div>
          <div className="quick-start-step">
            <span className="step-number">3</span>
            <p>Use your auth token as the Bearer token and specify the model alias</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
